/**
 * HealthAwarenessPage.jsx
 * Health Awareness page for QuitPact.
 *
 * User Story 3.2 - Health Awareness
 * AC 3.2.1: Displays at least three simple, evidence-based health insights
 *            about smoking in clear, non-technical language when the page loads.
 *
 * Team: TA37 - The Coders' Assembly
 */

import React, { useEffect, useRef } from 'react';
import { Activity, HeartPulse, Hourglass, Sparkles, Stethoscope, Wind } from 'lucide-react';
import './HealthAwarenessPage.css';

const INSIGHTS = [
  {
    id: 'lungs',
    icon: Wind,
    title: 'What smoking does to your lungs',
    description:
      'Every cigarette coats your airways in a sticky tar that your lungs struggle to clear. Over time, breathing becomes harder. Most smokers notice a persistent cough because the lungs are constantly trying to clean themselves.',
    borderColor: '#1d9e75',
  },
  {
    id: 'heart',
    icon: HeartPulse,
    title: 'How smoking affects your heart',
    description:
      'Smoking makes your blood thicker and your arteries narrower, so your heart has to work harder. This puts you at much higher risk of a heart attack or stroke, even if you feel perfectly healthy right now.',
    borderColor: '#e05c7a',
  },
  {
    id: 'longterm',
    icon: Hourglass,
    title: 'The long-term risks of smoking',
    description:
      'Smoking is the leading cause of preventable death in Australia. Long-term smokers are significantly more likely to develop serious illnesses including lung cancer, throat cancer, and chronic lung disease.',
    borderColor: '#ba7517',
  },
  {
    id: 'everyday',
    icon: Activity,
    title: 'Smoking changes everyday life',
    description:
      'Beyond major health risks, smoking affects the small things, like getting puffed walking up stairs, losing your sense of taste and smell, or having skin that ages faster.',
    borderColor: '#378add',
  },
  {
    id: 'quitting',
    icon: Sparkles,
    title: 'What happens when you quit',
    description:
      'Your body starts recovering within 20 minutes of your last cigarette. After one year without smoking, your risk of heart disease drops by half. Quitting at any age makes a real difference.',
    borderColor: '#2E7D32',
  },
];

const HealthAwarenessPage = () => {
  const cardsRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('insight-card--visible');
          } else {
            entry.target.classList.remove('insight-card--visible');
          }
        });
      },
      { threshold: 0.15 }
    );

    const cards = cardsRef.current?.querySelectorAll('.insight-card');
    cards?.forEach((card) => observer.observe(card));

    return () => observer.disconnect();
  }, []);

  return (
    <main className="health-awareness-page" aria-label="Health awareness">

      {/* -- Page header -- */}
      <header className="health-awareness-page__header">
        <div className="health-awareness-page__badge">
          <Stethoscope /> Health Awareness
        </div>
        <h1 className="health-awareness-page__heading">
          What smoking really does to your body
        </h1>
        <p className="health-awareness-page__subheading">
          Simple, honest facts; no scare tactics.
        </p>
      </header>

      {/* -- Stat banner -- */}
      <div className="health-awareness-page__stat-banner">
        <div className="health-awareness-page__stat">
          <span className="health-awareness-page__stat-number">1 in 2</span>
          <span className="health-awareness-page__stat-label">long-term smokers die from smoking</span>
        </div>
        <div className="health-awareness-page__stat-divider" />
        <div className="health-awareness-page__stat">
          <span className="health-awareness-page__stat-number">20 min</span>
          <span className="health-awareness-page__stat-label">for your body to start recovering after quitting</span>
        </div>
        <div className="health-awareness-page__stat-divider" />
        <div className="health-awareness-page__stat">
          <span className="health-awareness-page__stat-number">50%</span>
          <span className="health-awareness-page__stat-label">drop in heart disease risk after 1 year smoke-free</span>
        </div>
      </div>

      {/* -- Insight card grid - AC 3.2.1 -- */}
      <section
        className="health-awareness-page__grid"
        aria-label="Health insights"
        ref={cardsRef}
      >
        {INSIGHTS.map((insight, index) => {
          const InsightIcon = insight.icon;
          return (
          <article
            key={insight.id}
            id={insight.id}
            className="insight-card"
            aria-label={insight.title}
            style={{
              borderLeft: `4px solid ${insight.borderColor}`,
              transitionDelay: `${(index % 2) * 0.15}s`,
            }}
          >
            <div className="insight-card__icon" aria-hidden="true">
              <InsightIcon />
            </div>
            <h3 className="insight-card__title">{insight.title}</h3>
            <p className="insight-card__description">{insight.description}</p>
          </article>
          );
        })}
      </section>

      {/* -- CTA -- */}
      <section className="health-awareness-page__cta" aria-label="Next step">
        <p className="health-awareness-page__cta-text">
          Ready to understand your personal risk?
        </p>
        <a href="/check-in" className="health-awareness-page__cta-link">
          Take the check-in
        </a>
      </section>

    </main>
  );
};

export default HealthAwarenessPage;
